<template>
  <div class="cash-management-container">
    <TableList
      v-rect-loading="loading"
      class="cash-management-list"
      row-key="id"
      :title="$t(`${i18nPrefix}.title.cashManagement`)"
      :listData="companyBalanceList"
      :cellStyle="cellStyle"
      :tableColumns="tableColumns"
      :actionBtn="actionBtn"
      :showCreateBtn="false"
    >
      <template #filter>
        <el-input v-model="search.keyword" class="search-input" clearable :placeholder="$t(`${i18nPrefix}.placeholder.searchCompany`)" @input="handleKeywordsChange"></el-input>
      </template>
    </TableList>
    <DialogWrapper
      class="recharge-dialog"
      :show.sync="rechargeDialog.show"
      :loading="rechargeDialog.loading"
      @save="handleSaveRecharge"
      @closed="handleCancelRecharge"
    >
      <div class="company-recharge-content">
        <div class="balance">{{ $t(`${i18nPrefix}.label.companyCashBalanceTitle`, {companyName: rechargeDialog.companyName, balance: rechargeDialog.balance }) }}</div>
        <div class="operate-container">
          <div class="type-select">
            <span :class="rechargeDialog.type === '+' && 'select'" @click="rechargeDialog.type = '+'"><i class="el-icon-plus"/></span>
            <span :class="rechargeDialog.type === '-' && 'select'" @click="rechargeDialog.type = '-'"><i class="el-icon-minus"/></span>
          </div>
          <div class="info-container">
            <el-input
              class="input number-input points-input"
              v-model="rechargeDialog.rechargeCount"
              :placeholder="$t(`${i18nPrefix}.placeholder.rechargePoints`)"
              @input="handleRechargeChange"
              >
            </el-input>
            <el-input class="input note-input" :placeholder="$t(`${i18nPrefix}.placeholder.rechargeNotes`)" v-model="rechargeDialog.rechargeNotes"></el-input>
          </div>
        </div>
      </div>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { addI18nProperty } from '@/i18n'
import { formatBigNumber } from '@/components/filters'
import { COMPANY_REWARD_TYPE } from '@/constant/company'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix: 'admin.company.cashPointsManagement',
      loading: false,
      companyBalanceList: [],
      actionBtn: {
        recharge: {
          show: (row) => row.level !== 1,
          labelI18nKey: 'admin.company.cashPointsManagement.btn.recharge',
          onclick: this.handleRecharge
        },
        viewRechargeRecord: {
          show: true,
          labelI18nKey: 'admin.company.cashPointsManagement.btn.viewRechargeRecord',
          onclick: this.handleViewRechargeRecordList
        }
      },
      search: {
        keyword: null
      },
      rechargeCount: null,
      rechargeNotes: null,
      rechargeDialog: {
        show: false,
        loading: null,
        companyId: null,
        companyName: null,
        balance: 0,
        type: '+',
        rechargeCount: null
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.companyName`),
          width: '200',
          dataKey: 'companyI18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.referralExpireDate`),
          width: '100',
          dataKey: 'referral_expire_date',
          formatter: (val) => {
            return moment(val).format('YYYY/MM/DD')
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.balance`),
          width: '100',
          dataKey: 'referral_balance',
          formatter: (val) => {
            return formatBigNumber(val)
          }
        }
      ]
      return columns
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('cashPointsManagement', ['getCompanyReferralCashBalanceInfo', 'updateCompanyRecharge']),
    initPage () {
      this.loadCompanyReferralBalanceInfo()
    },
    async loadCompanyReferralBalanceInfo () {
      this.loading = true
      try {
        const params = {
          keyword: this.search.keyword
        }
        const resp = await this.getCompanyReferralCashBalanceInfo(params)
        if (resp) {
          this.companyBalanceList = this.flattenTreeFromParentIds(resp)
        }
      } finally {
        this.loading = false
      }
    },
    flattenTreeFromParentIds (items) {
      const itemMap = _.keyBy(items, 'id')
      _.forEach(items, node => {
        node.children = []
      })
      const roots = []
      _.forEach(items, node => {
        // parent_ids中除自身外的第一个存在的父节点
        const parentId = _.find(node.parent_ids, pid => pid !== node.id && _.has(itemMap, pid))
        const parent = _.get(itemMap, parentId, null)

        if (parent) {
          parent.children.push(node)
        } else {
          // 没有父节点或所有父节点不存在，则为根节点
          roots.push(node)
        }
      })

      // 父节点在前、子节点在后
      const result = []
      // 将node 节点所有子节点放在node 后面
      function traverse (node) {
        let cnNameKey = 'cn_name'
        if (!_.get(node, cnNameKey)) {
          cnNameKey = 'name'
        }
        addI18nProperty(node, 'companyI18nName', cnNameKey, 'name')
        node.level = _.filter(node.parent_ids, pid => _.has(itemMap, pid)).length
        // 移除children属性
        result.push(_.omit(node, 'children'))
        _.forEach(node.children, traverse)
      }
      _.forEach(roots, traverse)

      return result
    },
    cellStyle ({ row, columnIndex }) {
      if (columnIndex === 0) {
        return { paddingLeft: (row.level - 1) * 15 + 'px' }
      }
    },
    handleRecharge (row) {
      this.rechargeDialog.companyId = row.id
      this.rechargeDialog.companyName = row.companyI18nName
      this.rechargeDialog.balance = row.referral_balance
      this.rechargeDialog.show = true
    },
    handleRechargeChange (value) {
      const number = value?.replace(/[^0-9]/g, '')
      this.rechargeDialog.rechargeCount = number && formatBigNumber(_.replace(number.toString(), /,/g, ''))
    },
    handleCancelRecharge () {
      this.rechargeDialog.show = false
      this.rechargeCount = null
      this.rechargeNotes = null
      this.rechargeDialog.companyId = null
      this.rechargeDialog.companyName = null
      this.rechargeDialog.balance = 0
      this.rechargeDialog.rechargeCount = null
      this.rechargeDialog.rechargeNotes = null
    },
    async handleSaveRecharge () {
      let rechargeCount = _.chain(this.rechargeDialog.rechargeCount).replace(/[,']/g, '').toNumber().value()
      if (this.rechargeDialog.type === '-') {
        rechargeCount = -rechargeCount
      }
      if (this.rechargeDialog.balance + rechargeCount < 0) {
        this.$message({
          type: 'warning',
          message: this.$t(`${this.i18nPrefix}.message.rechargeNotice`)
        })
        return
      }
      const params = {
        reward_type: COMPANY_REWARD_TYPE.CASH,
        company_id: this.rechargeDialog.companyId,
        change: rechargeCount,
        note: this.rechargeDialog.rechargeNotes
      }
      this.rechargeDialog.loading = true
      try {
        const resp = await this.updateCompanyRecharge(params)
        if (resp && resp.success) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.rechargeSuccess`)
          })
          this.handleCancelRecharge()
          this.loadCompanyReferralBalanceInfo()
        } else {
          this.$message({
            type: 'error',
            message: this.$t(`${this.i18nPrefix}.message.rechargeFail`)
          })
        }
      } finally {
        this.rechargeDialog.loading = false
      }
    },
    handleViewRechargeRecordList (row) {
      window.open(`/v-admin/referral/cash-management/${row.id}/recharge-record`)
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.loadCompanyReferralBalanceInfo, 1000)
      }
      this.keywordsSearchDebounce()
    }
  }
}
</script>

<style lang="scss" scoped>
.cash-management-container {
  .cash-management-list {
    .search-input {
      width: 278px;
    }
  }

  .recharge-dialog {
    .company-recharge-content {
      .balance {
        margin-bottom: 14px;
        font-size: 22px;
        color: #303133;
      }
      .operate-container {
        margin-top: 30px;
        text-align: center;
        display: flex;
        justify-content: flex-start;

        .type-select {
          display: flex;
          color: #606266;
          margin-right: 20px;

          > span {
            width: 32px;
            height: 32px;
            font-size: 18px;
            line-height: 32px;
            border: 1px solid #E4E7ED ;
            border-radius: 2px;
            cursor: pointer;

            > i {
              font-weight: bold;
            }

            &:first-child {
              margin: 0;
              border-right: 0;
            }
          }

          .select {
            color: #0096cc;
          }
        }
        .info-container {
          text-align: left;
          .input {
            margin-bottom: 15px;
          }
        }
      }
    }
  }
}
</style>
